import { useEffect, useMemo } from "react";
import { Select } from "antd";
import {
  useEffectiveRole,
  useEffectiveUserId,
} from "../../hooks/useEffectiveRole";
import { useOrgUsers } from "../../context/OrgUsersContext";

// Shared user-assignment picker — MANY-TO-MANY.
//
// Single component, reused in 5 places:
//   - AddClientModal (create flow)
//   - ClientView (edit flow)
//   - Single-record reassign modal
//   - User-deactivation modal (admin chooses "reassign all to X")
//   - Excel import preview (read-only display via `disabled` prop)
//
// A client can be assigned to ZERO or MORE users. The component is a
// MULTI-select; its `value`/`onChange` use a plain array of user-ids:
//   - []            → Shared pool (no one assigned)
//   - [<uuid>, ...] → assigned to those users
//   - null/undefined → "no value yet" (treated as default per role)
//
// Empty array == Shared, so there is no sentinel option anymore (the old
// single-select needed "unassigned" because AntD couldn't round-trip a
// null option value — a multi-select with an empty array models Shared
// natively). Callers forward the array straight to the BE as
// `assigned_user_ids` via the exported `toApiValue()` helper.
//
// Render rules:
//
//   | Caller's role  | Org user count | Options shown                  |
//   | -------------- | -------------- | ------------------------------ |
//   | any            | 1 (loaded)     | render NOTHING (hideWhenSingleUser default true) |
//   | user           | >1             | Myself only (regular users may only assign themselves) |
//   | tenant_admin+  | >1             | All enabled users              |
//
// Clearing all tags == Shared pool. There is no explicit "Shared" option;
// an empty selection is Shared.
//
// While the org-user count is still loading, the component renders nothing.
//
// Typeahead is implicit in multi-select; we keep client-side filtering over
// the cached OrgUsersContext list (capped at 200) rather than a debounced
// server hit.

// Submit-side translation. Forward the picker's value to a
// create/update/transfer endpoint as `assigned_user_ids`.
//   value is an array of uuids → return the same array ([] = Shared).
//   value undefined/null       → return [] (no selection → BE applies role default
//                                 only when the field is OMITTED; callers that want
//                                 the default should not send the field at all).
export const toApiValue = (selectValue) => {
  if (Array.isArray(selectValue)) return selectValue;
  if (selectValue === undefined || selectValue === null || selectValue === "")
    return [];
  // Tolerate a stray scalar (legacy callers) by wrapping it.
  return [selectValue];
};

// Reverse direction: BE returns `assigned_users: [{user_id,...}, ...]`
// (empty list = Shared). Render that in the Select as an array of ids.
// Accepts either the array-of-objects shape or a bare array of ids.
export const fromApiValue = (apiValue) => {
  if (!apiValue) return [];
  if (Array.isArray(apiValue)) {
    return apiValue.map((u) =>
      typeof u === "string" ? u : u?.user_id ?? u?.id,
    ).filter(Boolean);
  }
  return [];
};

const TYPEAHEAD_THRESHOLD = 20;

// Map any portal-name flavor a caller might pass → the access-flag
// field name returned by GET /access/users. Three callers use three
// different shapes (Clients.jsx ServiceType, AddClientModal type,
// BE-style snake), so normalise here rather than at every call site.
//
// The field's value is EFFECTIVE access (user-flag AND org-flag) as
// computed server-side — so a teammate with personal IT access but
// whose org has IT disabled is correctly hidden here without the FE
// having to know about the org-level flag.
export const PORTAL_TO_FLAG = {
  // ServiceType / URL segments
  "income-tax": "enable_income_tax_portal",
  income_tax: "enable_income_tax_portal",
  "gstin": "enable_gst_portal",
  gst: "enable_gst_portal",
  GST: "enable_gst_portal",
  tds: "enable_tds_portal",
  TDS: "enable_tds_portal",
  // AddClientModal `type` literal for IT
  "income-Tax": "enable_income_tax_portal",
};

const UserAssignmentSelect = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  size = "middle",
  // When true (default), render nothing if the org has exactly 1 user.
  // The Reassign / Deactivation modal call sites override to false: they
  // only ever mount in multi-user contexts (admin disabling someone
  // implies >=2 users) and want the field rendered explicitly.
  hideWhenSingleUser = true,
  // Optional override of the "self" label (only shown to regular users).
  // Defaults to "Myself".
  selfLabel = "Myself",
  // Placeholder shown when nothing is selected (= Shared pool).
  sharedLabel = "Shared (no one)",
  // Allow callers to ALWAYS show the field even outside the canonical
  // 5 sites — explicitly off by default to discourage spec-creep.
  // (No internal use; here as an escape hatch for the deactivation modal
  // which mounts before context refreshes from a disable attempt.)
  forceShow = false,
  // Portal scope — when provided, only users who have the matching
  // `enable_<portal>_portal` flag true appear in the dropdown. Lets
  // the IT clients page hide GST-only / TDS-only team members from
  // the assign pickers (and same for GST/TDS pages). Pass a
  // ServiceType ("gstin"/"income-tax"/"tds") or AddClientModal type
  // ("GST"/"TDS"/"income-tax") — both flavours are normalised.
  // null/undefined → no portal filter (preserves pre-2026-05-13 behaviour).
  portal = null,
}) => {
  // Role + user-id via the shared hook — embed JWT claims take precedence
  // inside the /embed/* iframe so the "Myself" (regular) vs "all users"
  // (admin) branches render for the right identity in both surfaces, even
  // when the browser holds a stale localStorage session for someone else.
  const effectiveRole = useEffectiveRole();
  const effectiveUserId = useEffectiveUserId();
  const { users, loaded, loading, isSingleUserOrg, ensureLoaded } =
    useOrgUsers();

  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  // Resolve portal-name flavor → user-row flag field. `null` keeps
  // the legacy (no-filter) behaviour for callers that don't pass it.
  const portalFlagField = portal ? PORTAL_TO_FLAG[portal] || null : null;

  // Build the option list once per `users` change. Memoised because list
  // pages with this component in every row would otherwise rebuild it on
  // every re-render.
  const options = useMemo(() => {
    const isRegularUser =
      effectiveRole === "user" || effectiveRole === "read_only";
    const opts = [];
    if (isRegularUser) {
      // Regular user: only Myself. Backend would 400 anything else anyway
      // — hiding it avoids a confusing dead-end UX. Clearing the tag =
      // Shared (empty array).
      if (effectiveUserId) {
        opts.push({ value: effectiveUserId, label: selfLabel });
      }
    } else {
      // Admin: all enabled assignable users — additionally filtered by
      // per-portal access when `portal` is passed. Members without the
      // current portal enabled don't appear (so an IT-only org can't
      // accidentally assign an IT client to a GST-only teammate).
      for (const u of users) {
        if (portalFlagField && u[portalFlagField] === false) continue;
        opts.push({
          value: u.id,
          label: u.full_name ? `${u.full_name} (${u.email})` : u.email,
        });
      }
    }
    // No explicit "Shared" option — an empty selection IS Shared.
    return opts;
  }, [
    users,
    effectiveRole,
    effectiveUserId,
    selfLabel,
    portalFlagField,
  ]);

  // Hide-when-single-user is the spec's "1 user → no UI". Loaded check
  // matters: while we're still fetching, isSingleUserOrg is false and we
  // would render the (admin) "Shared (no one)" option even in a true
  // single-user org for one frame. Defer rendering until loaded.
  if (!forceShow) {
    if (!loaded) return null;
    if (hideWhenSingleUser && isSingleUserOrg) return null;
  }

  const isRegularUser =
    effectiveRole === "user" || effectiveRole === "read_only";
  // Multi-select always shows a searchable tag input; enable client-side
  // filtering for admins with longer lists.
  const useTypeahead = !isRegularUser && options.length > TYPEAHEAD_THRESHOLD;

  return (
    <Select
      mode="multiple"
      value={Array.isArray(value) ? value : value ? [value] : []}
      onChange={onChange}
      options={options}
      disabled={disabled || loading}
      size={size}
      placeholder={placeholder ?? sharedLabel}
      style={{ width: "100%" }}
      // Client-side filter over the cached list (no debounced server hit;
      // see file-top comment).
      showSearch
      filterOption={(input, option) => {
        const label = (option?.label ?? "").toLowerCase();
        return label.includes(input.toLowerCase());
      }}
      // Clearing all tags == Shared pool.
      allowClear
      // Numeric cap instead of `responsive` — responsive measures whether
      // a tag's full width fits inside the trigger, and on the narrower
      // modal trigger boxes (e.g. AddClientModal) even a single tag like
      // "Hemant Tayal (ptayal793@gmail.com)" overflows, collapsing the
      // visible selection to "+ 1 ..." and hiding the user's identity.
      // A flat cap of 2 keeps the first two assignees always visible
      // (each truncating at the trigger edge if necessary); the 3rd+ go
      // into the standard "+ N" affordance.
      maxTagCount={2}
      // Cap an individual tag's text so a long email doesn't push the
      // trigger out of its column. Most full_name (~22 chars) +
      // email-in-parens (~28 chars) labels stay readable; beyond ~40
      // chars an ellipsis kicks in inside the tag itself.
      maxTagTextLength={40}
      // Mount the dropdown inside the trigger's parent (the Form.Item
      // container) instead of document.body. When this component is
      // rendered inside an AntD Modal the default body-mount triggers a
      // reposition loop: Modal applies a scroll-lock padding-right to
      // body → Select recalculates its anchor → body re-paints → Select
      // recalculates again → visible flicker on the modal AND the page
      // behind it. Mounting in-trigger sidesteps the interaction
      // entirely. Safe for non-modal call sites too — the dropdown just
      // renders in the normal flow above the trigger.
      getPopupContainer={(triggerNode) =>
        triggerNode?.parentNode || document.body
      }
    />
  );
};

export default UserAssignmentSelect;
